// License token verification — runs in the BROWSER and in tests.
// Uses `jose` on top of WebCrypto Ed25519. Multiple public keys are
// supported by `kid` (hash map or /license/jwks) so keys can rotate.

import { jwtVerify, importJWK, type JWTPayload } from 'jose';
import type { LicenseClaims, SubscriptionStatus } from './types';
import { licenseClockSane, licenseInGrace, licenseUsable } from './types';

// kid -> public JWK (x only, Ed25519 curve)
export type JwkByKid = Record<string, { kty: string; crv: string; x: string; alg?: string }>;

export type VerifyReason =
  | 'signature'
  | 'expired'
  | 'malformed'
  | 'unknown-kid'
  | 'clock-skew'
  | 'wrong-device';

export type VerifyResult =
  | { ok: true; claims: LicenseClaims }
  | { ok: false; reason: VerifyReason };

export async function fetchJwks(jwksUrl: string): Promise<JwkByKid> {
  const res = await fetch(jwksUrl);
  if (!res.ok) throw new Error(`jwks http ${res.status}`);
  const data = (await res.json()) as { keys?: Array<JwkByKid[''] & { kid?: string }> };
  const map: JwkByKid = {};
  for (const k of data.keys ?? []) {
    if (k.kid) map[k.kid] = k;
  }
  return map;
}

export async function verifyLicenseToken(
  token: string,
  jwks: JwkByKid,
  opts: { deviceId: string; lastSeenServerTime?: number; now?: number },
): Promise<VerifyResult> {
  const now = opts.now ?? Math.floor(Date.now() / 1000);
  let kid: string | undefined;
  let decoded: JWTPayload | null = null;
  try {
    const header = JSON.parse(b64UrlDecode(token.split('.')[0])) as { kid?: string };
    kid = header.kid;
    decoded = JSON.parse(b64UrlDecode(token.split('.')[1])) as JWTPayload;
  } catch {
    return { ok: false, reason: 'malformed' };
  }
  if (!kid || !(kid in jwks)) return { ok: false, reason: 'unknown-kid' };

  // jose v6 re-throws on expired exp even with ignoreExpiration; instead we
  // verify the SIGNATURE with `now` inside the token's validity window and
  // apply exp/grace semantics ourselves (licenseUsable / licenseInGrace).
  const exp = typeof decoded?.exp === 'number' ? decoded.exp : Number.POSITIVE_INFINITY;
  try {
    const key = await importJWK(jwks[kid], 'EdDSA');
    const { payload } = await jwtVerify(token, key, {
      algorithms: ['EdDSA'],
      currentDate: new Date(Math.min(now, exp - 60) * 1000),
    });
    const parsed = claimsFromPayload(payload);
    if (!parsed) return { ok: false, reason: 'malformed' };
    if (!licenseClockSane(parsed, opts.lastSeenServerTime ?? NaN)) {
      return { ok: false, reason: 'clock-skew' };
    }
    if (parsed.did !== opts.deviceId) return { ok: false, reason: 'wrong-device' };
    if (licenseUsable(parsed, now)) return { ok: true, claims: parsed };
    if (licenseInGrace(parsed, now)) return { ok: true, claims: parsed };
    return { ok: false, reason: 'expired' };
  } catch {
    return { ok: false, reason: 'signature' };
  }
}

export function claimsFromPayload(payload: JWTPayload): LicenseClaims | null {
  const did = payload['did'];
  const plan = payload['plan'];
  const status = payload['status'];
  const entitlements = payload['entitlements'] as
    | { export: boolean; watermark: boolean }
    | undefined;
  const gu = payload['gu'];
  const tee = payload['tee'];
  if (
    typeof payload.sub !== 'string' ||
    typeof did !== 'string' ||
    typeof plan !== 'string' ||
    typeof status !== 'string' ||
    !entitlements ||
    typeof entitlements.export !== 'boolean' ||
    typeof entitlements.watermark !== 'boolean' ||
    typeof payload.iat !== 'number' ||
    typeof payload.exp !== 'number' ||
    typeof gu !== 'number'
  ) {
    return null;
  }
  return {
    sub: payload.sub,
    did,
    plan,
    status: status as SubscriptionStatus,
    entitlements: { export: entitlements.export, watermark: entitlements.watermark },
    iat: payload.iat,
    exp: payload.exp,
    gu,
    ...(typeof tee === 'number' ? { tee } : {}),
  };
}

function b64UrlDecode(part: string): string {
  const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
  return atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
}
